import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Save, Share2, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Avatar, Btn, Confirm, Empty, Field, IconBtn, Loading, Page, SearchBox, Section, Select, StatusPill } from "../components/wf";
import { api, copyLink } from "../lib/api";
import { usePerms } from "../lib/auth";
import { useCustomers, useKastle } from "../lib/queries";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";
import { AddressField } from "../components/AddressField";

const customerBody = ({ id: _id, ...rest }) => ({ ...rest, billing_rate: parseFloat(rest.billing_rate) || 0 });

export function Customers() {
  const q = useCustomers();
  const nav = useNavigate();
  const [s, setS] = useState("");
  const data = useMemo(() => (q.data ?? []).filter((c) => `${c.name} ${c.address} ${c.city}`.toLowerCase().includes(s.toLowerCase())), [q.data, s]);
  return (
    <Page title="Customers" subtitle={`${q.data?.length ?? 0} customers`} wide testid="customers-page"
      right={<Btn icon={Plus} onClick={() => nav("/customers/new")} data-testid="add-customer-button">New Customer</Btn>}>
      <SearchBox testid="customers-search-input" value={s} onChange={setS} placeholder="Search customers" />
      {q.isLoading ? <Loading /> : !data.length ? <Empty icon={Building2} title="No customers found" subtitle="Click New Customer to add one." /> : (
        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="customers-list">
          {data.map((c, i) => (
            <Link key={c.id} to={`/customers/${c.id}`} data-testid={`customer-row-${c.id}`} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}
              className="wf-rise flex items-center gap-3 rounded-2xl border border-wf-line p-4 transition-colors hover:border-wf-brand2 hover:bg-wf-s2">
              <Avatar name={c.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{c.name}</p>
                <p className="truncate text-sm text-wf-mute">{[c.address, c.city, c.state].filter(Boolean).join(", ") || "No address"}</p>
              </div>
              {c.kastle_customer_number && <span className="text-xs font-semibold text-wf-mute">#{c.kastle_customer_number}</span>}
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

const emptyCustomer = { name: "", address: "", building_floors: "", city: "", state: "", phone: "", contact: "", contact_phone: "", contact_email: "", kastle_customer_number: "", kastle_pm_id: "", billing_rate: "" };

export function CustomerForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isManager } = usePerms();
  const kastle = useKastle();
  const existing = useQuery({ queryKey: ["customer", id], queryFn: () => api(`/customers/${id}`), enabled: !!id });
  const jobs = useQuery({ queryKey: ["work-orders", { customer: id }], queryFn: () => api(`/work-orders?customer_id=${id}`), enabled: !!id });
  const [f, setF] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    const c = existing.data;
    if (c && !loaded) {
      setF({ ...emptyCustomer, ...c, billing_rate: c.billing_rate ? String(c.billing_rate) : "", kastle_pm_id: c.kastle_pm_id ?? "" });
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  const auto = useAutoSave(f, async (d) => {
    const res = await api(`/customers/${id}`, { method: "PUT", body: customerBody(d) });
    qc.setQueryData(["customer", id], res);
    qc.invalidateQueries({ queryKey: ["customers"] });
  }, { ready: !!id && loaded, enabled: !!f.name.trim() });
  const draft = useDraft(`draft:customer:${params.get("pick") ? "pick" : "new"}`, f, setF, !id);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter the customer name");
    setSaving(true);
    try {
      const res = await api(id ? `/customers/${id}` : "/customers", { method: id ? "PUT" : "POST", body: customerBody(f) });
      if (!id) draft.clear();
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.setQueryData(["customer", res.id], res);
      toast.success(id ? "Customer updated" : "Customer added");
      if (params.get("pick")) nav(`/work-orders/new?customer=${res.id}`, { replace: true });
      else nav(id ? -1 : `/customers/${res.id}`, { replace: !id });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api(`/customers/${id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Customer deleted");
      nav("/customers");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (id && existing.isLoading) return <Loading />;
  return (
    <Page title={id ? f.name || "Customer" : "New Customer"} back testid="customer-form"
      right={<>
        {id && isManager && <IconBtn icon={Trash2} tone="danger" data-testid="customer-delete-button" onClick={() => setConfirm(true)} />}
        {id ? <SaveStatus status={auto.status} /> : <Btn icon={Save} loading={saving} onClick={save} data-testid="customer-save-button">Save</Btn>}
      </>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setF(emptyCustomer); }} />
      <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
        <div>
          <Field label="Customer Name *" testid="customer-name-input" value={f.name} onChange={set("name")} placeholder="Acme Corp" />
          <AddressField value={f.address} onChange={set("address")}
            onSelected={(a) => setF((s) => ({ ...s, address: a.street || a.formattedAddress, city: a.city || s.city, state: a.state || s.state }))} />
          <Field label="Building / Floor(s)" testid="customer-building-input" value={f.building_floors} onChange={set("building_floors")} placeholder="Tower B, Floors 3-5" />
          <div className="grid gap-x-3 sm:grid-cols-2">
            <Field label="City" testid="customer-city-input" value={f.city} onChange={set("city")} />
            <Field label="State" testid="customer-state-input" value={f.state} onChange={set("state")} />
          </div>
          <Field label="Phone Number" testid="customer-phone-input" type="tel" value={f.phone} onChange={set("phone")} />
          {isManager && <Field label="Billing Rate per Man Hour ($)" type="number" step="0.01" testid="customer-billing-rate-input" value={f.billing_rate} onChange={set("billing_rate")} />}
          <Section title="Contact" />
          <Field label="Contact Name" testid="customer-contact-input" value={f.contact} onChange={set("contact")} />
          <div className="grid gap-x-3 sm:grid-cols-2">
            <Field label="Contact Phone" testid="customer-contact-phone-input" value={f.contact_phone} onChange={set("contact_phone")} />
            <Field label="Contact Email" type="email" testid="customer-contact-email-input" value={f.contact_email} onChange={set("contact_email")} />
          </div>
          <Section title="Kastle" />
          <Field label="Kastle Customer Number" testid="customer-kastle-number-input" value={f.kastle_customer_number} onChange={set("kastle_customer_number")} />
          <Select label="Kastle Project Manager" testid="customer-kastle-pm-select" value={f.kastle_pm_id} placeholder="None"
            options={(kastle.data ?? []).map((k) => ({ value: k.id, label: [k.name, k.location].filter(Boolean).join(" · ") }))} onChange={set("kastle_pm_id")} />
        </div>
        {id && (
          <div>
            <Section title={`Jobs (${jobs.data?.length ?? 0})`} actionLabel="New Job" testid="customer-new-job" onAction={() => nav(`/work-orders/new?customer=${id}`)} />
            <Btn size="sm" variant="ghost" icon={Share2} data-testid="customer-share-photos"
              onClick={() => copyLink(`/customers/${id}/share-link`).then(() => toast.success("Photos link copied")).catch((e) => toast.error(e.message))}>Share all photos link</Btn>
            <div className="mt-2 flex flex-col gap-2" data-testid="customer-jobs">
              {(jobs.data ?? []).map((j) => (
                <Link key={j.id} to={`/work-orders/${j.id}`} data-testid={`customer-job-${j.id}`} className="rounded-xl border border-wf-line p-3 hover:bg-wf-s2">
                  <div className="flex items-center justify-between gap-2"><p className="truncate text-sm font-bold">{j.job_description || "No description"}</p><StatusPill label={j.status} /></div>
                  <p className="text-xs text-wf-mute">{new Date(j.created_at).toLocaleDateString()}</p>
                </Link>
              ))}
              {jobs.data?.length === 0 && <p className="text-sm text-wf-mute">No jobs for this customer yet.</p>}
            </div>
          </div>
        )}
      </div>
      <Confirm open={confirm} title="Delete this customer?" message="It can be restored from Recently Deleted for 30 days." onConfirm={remove} onClose={() => setConfirm(false)} />
    </Page>
  );
}
